import React, { useState, useEffect } from 'react';
import { Form } from 'react-bootstrap';
import axios from '../../../axios';
import './bot-detection.css';

/**
 * Bot Detection Control Component
 * 
 * A React component for controlling company-specific bot detection settings
 * Features:
 * - Enable/Disable bot detection for the company
 * - Documentation display showing protection features
 * - Real-time status updates
 * 
 * Bot Detection Features (when enabled):
 * - Company-specific control via query parameters (rid/key)
 * - User agent detection (Cisco tools, GoogleImageProxy, automation tools)
 * - IP range blocking (dedicated Cisco IP blocking function)
 * - Rate limiting protection
 * - Security tool detection and blocking
 * - Headless browser detection
 * - Google/Microsoft services blocking
 * - Advanced threat intelligence and scoring
 */

const BotDetection = ({
    statusEndpoint = '/api/company/bot-detection',
    className = '',
    title = 'Bot Detection Control'
}) => {
    const [botDetectionEnabled, setBotDetectionEnabled] = useState(false);
    const [statusLoading, setStatusLoading] = useState(false);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);

    // Comprehensive bot detection documentation data
    const protectionFeatures = [
        {
            category: 'Enterprise Network Infrastructure Protection',
            features: [
                'Cisco Systems Comprehensive Blocking: All public IP ranges (128.107.x.x, 64.100.x.x, 171.68-70.x.x, 192.135.x.x, 207.172.x.x), Umbrella DNS security, Stealthwatch network monitoring, Meraki cloud-managed infrastructure, ASA firewalls, Firepower threat defense',
                'FireEye/Mandiant Intelligence Blocking: Threat intelligence feeds (199.19.248-249.x.x), analysis services (162.220-223.x.x), cloud infrastructure (170.224-225.x.x), Mandiant incident response tools, malware analysis sandboxes',
                'Company-specific bot detection control via query parameters (rid/key validation with database lookup)',
                'Advanced rate limiting with configurable thresholds (default: 50 req/min, 60-second windows) and IP-based tracking',
                'Intelligent request pattern analysis with suspicious behavior detection and automated threat response'
            ]
        },
        {
            category: 'Comprehensive Security Vendor Detection',
            features: [
                'Antivirus Solutions: Kaspersky Security (65.200.x.x), Symantec/Broadcom Endpoint Protection, McAfee/Trellix MVISION, Norton/NortonLifeLock, Avast Business, AVG Technologies, Avira Operations, Bitdefender GravityZone, ESET NOD32, Sophos Intercept X',
                'Advanced Threat Protection (ATP): CrowdStrike Falcon platform, SentinelOne Singularity XDR, Carbon Black (VMware) threat hunting, Cylance AI-powered protection, Endgame endpoint detection, Microsoft Defender ATP, Windows ATP integration',
                'Vulnerability Assessment Tools: Tenable Nessus Professional, OpenVAS Community Edition, Qualys VMDR cloud platform, Rapid7 Nexpose/InsightVM, Acunetix Web Security Scanner, IBM AppScan security testing, Checkmarx static analysis, Veracode dynamic testing',
                'Penetration Testing Frameworks: Metasploit Pro exploitation, Burp Suite Professional, OWASP ZAP security testing, Nikto web scanner, SQLMap injection testing, NMAP network discovery, W3AF web application attacks, BeEF browser exploitation',
                'Enterprise Firewalls & Network Security: Fortinet FortiGate (64.102.x.x), Checkpoint Harmony (208.91.x.x), Palo Alto Networks Prisma, SonicWall NSA series, WatchGuard Firebox, Juniper SRX series, pfSense community edition, Untangle NG Firewall'
            ]
        },
        {
            category: 'Cloud Platform & Automation Detection',
            features: [
                'Google Services Comprehensive Blocking: GoogleBot crawlers (66.249.x.x, 66.102.x.x), Google Cloud security scanners (34.x.x.x, 35.x.x.x), APIs-Google automated services, Google SafeBrowsing checks, Google Analytics data collection, Google Ads crawlers, Chrome Lighthouse audits',
                'Microsoft/Azure Services Protection: BingBot crawlers (40.x.x.x, 52.x.x.x, 65.52-55.x.x), Microsoft Azure security services, Windows Defender scanning, Office 365 ATP protection, Azure Application Gateway, Microsoft Security Response Center tools',
                'Amazon Web Services (AWS): Inspector vulnerability assessment (52.x.x.x), GuardDuty threat detection, AWS Security Hub scanning, CloudTrail monitoring, EC2 automated services (54.x.x.x), Lambda function calls, CloudFront edge locations',
                'Automation Framework Detection: Selenium WebDriver (headless Chrome/Firefox), Puppeteer Node.js automation, Playwright cross-browser testing, PhantomJS headless browsing, Chrome DevTools Protocol, Firefox Marionette protocol',
                'API Testing & Development Tools: Postman automated testing, Insomnia REST client, HTTPie command-line client, curl/wget utilities, Python requests library, Node.js HTTP modules, Go HTTP clients, Java OkHTTP'
            ]
        },
        {
            category: 'Advanced Bot & Crawler Analysis',
            features: [
                'User Agent Pattern Recognition: 200+ known bot signatures including search engine crawlers, social media bots (FacebookExternalHit, Twitterbot, LinkedInBot), monitoring services (Pingdom, Nagios, Zabbix), SEO tools (Screaming Frog, Ahrefs, SEMrush)',
                'Headless Browser Detection: Chrome headless mode identification, Firefox headless indicators, WebDriver automation signals, missing browser headers analysis, JavaScript execution environment detection, viewport and screen resolution anomalies',
                'Command Line Tool Identification: curl user-agent patterns, wget download utilities, Python requests/urllib libraries, PowerShell Invoke-WebRequest, Apache HTTP client, .NET HttpClient, Ruby HTTP libraries, PHP cURL implementations',
                'Suspicious Request Pattern Analysis: Missing Accept-Language headers, unusual Accept-Encoding values, absent or minimal cookie support, non-standard HTTP methods (TRACE, TRACK, DEBUG), rapid sequential requests, identical timing patterns',
                'Behavioral Analysis Engine: Request frequency anomalies, unusual navigation patterns, missing referrer information, consistent timing intervals, identical session characteristics, automated form submission patterns'
            ]
        },
        {
            category: 'Enterprise Threat Intelligence & Analytics',
            features: [
                'Real-time Geolocation Intelligence: IP address country/region verification, datacenter IP detection, VPN/proxy identification, Tor exit node blocking, suspicious geographic patterns, ASN-based filtering, ISP reputation analysis',
                'Advanced Threat Scoring Algorithm: Multi-factor risk assessment (0-100 scale), user agent analysis weighting, IP reputation integration, request pattern scoring, header fingerprint analysis, behavioral anomaly detection, temporal pattern recognition',
                'SIEM Integration Capabilities: Splunk Enterprise log forwarding, QRadar security intelligence, ArcSight ESM integration, Elastic Security (ELK stack), LogRhythm NextGen SIEM, IBM Security QRadar, RSA NetWitness Platform, Sumo Logic cloud SIEM',
                'Comprehensive Logging & Monitoring: Real-time blocked attempt tracking, threat categorization and tagging, company-specific analytics dashboard, geographic threat mapping, vendor-specific blocking statistics, false positive rate monitoring',
                'Database Analytics & Reporting: Blocked attempts aggregation by company, threat trend analysis over time, top blocked IP addresses and user agents, security vendor activity patterns, peak attack time identification, custom reporting capabilities'
            ]
        }
    ];

    // Fetch bot detection status
    const fetchBotDetectionStatus = async () => {
        try {
            setLoading(true);
            const response = await axios.get(statusEndpoint);
            if (response.data.success) {
                setBotDetectionEnabled(response.data.isBotDetection || false);
                setError(null);
            } else {
                throw new Error(response.data.message || 'Failed to fetch status');
            }
        } catch (err) {
            console.error('Error fetching bot detection status:', err);
            setError(err.response?.data?.message || err.message);
        } finally {
            setLoading(false);
        }
    };

    // Toggle bot detection status
    const toggleBotDetection = async () => {
        if (statusLoading) return;
        
        setStatusLoading(true);
        try {
            const newStatus = !botDetectionEnabled;
            const response = await axios.post(statusEndpoint, {
                status: newStatus
            });

            if (response.data.success) {
                setBotDetectionEnabled(newStatus);
                setError(null);
            } else {
                throw new Error(response.data.message || 'Failed to update bot detection status');
            }
        } catch (err) {
            console.error('Error toggling bot detection:', err);
            setError(err.response?.data?.message || err.message);
        } finally {
            setStatusLoading(false);
        }
    };

    // Initial status fetch
    useEffect(() => {
        fetchBotDetectionStatus();
    }, []);

    if (loading) {
        return (
            <div className="tab-wrap">
                <div className="loading-spinner">
                    <div className="spinner"></div>
                    <p>Loading bot detection settings...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="tab-wrap">
                <div className="error-message">
                    <h6>Error Loading Bot Detection Settings</h6>
                    <p>{error}</p>
                    <button onClick={fetchBotDetectionStatus} className="retry-button">
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="tab-wrap">
            <div className='tabHeading'>
                <h6>Enterprise Bot Detection Management</h6>
                <p>Comprehensive protection against automated threats, security scanners, and malicious bots with enterprise-grade vendor IP blocking.</p>
            </div>

            <div className="mb-3">
                <Form.Check
                    type="switch"
                    label="Enable Advanced Bot Detection"
                    id="bot-detection-switch"
                    checked={botDetectionEnabled}
                    onChange={toggleBotDetection}
                    disabled={statusLoading}
                />
                {statusLoading && <span className="status-loading">Updating security settings...</span>}
            </div>

            {botDetectionEnabled && (
                <div className="bot-detection-content">
                    <div className="protection-status-section">
                        <div className="status-card enabled">
                            <div className="status-icon"><span>🛡️</span></div>
                            <div className="status-content">
                                <h6>Enterprise Protection Active</h6>
                                <p>Your company is protected from automated threats, security scanners, and malicious bots with comprehensive vendor blocking.</p>
                            </div>
                        </div>
                    </div>

                    <div className="features-documentation">
                        <h6 className="section-title">Advanced Protection Features</h6>
                        <div className="features-grid">
                            {protectionFeatures.map((category, index) => (
                                <div key={index} className="feature-category">
                                    <h6 className="category-title">{category.category}</h6>
                                    <ul className="feature-list">
                                        {category.features.map((feature, featureIndex) => (
                                            <li key={featureIndex} className="feature-item">
                                                <span className="feature-icon">✓</span>
                                                {feature}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="technical-implementation">
                        <h6 className="section-title">Enterprise Security Implementation</h6>
                        <div className="tech-details">
                            <div className="tech-item">
                                <strong>Company-Specific Control:</strong>
                                <span>Bot detection is controlled per company using query parameters (rid/key) to identify the requesting company and check their isBotDetection settings in the database.</span>
                            </div>
                            <div className="tech-item">
                                <strong>Cisco & FireEye Blocking:</strong>
                                <span>Dedicated protection with comprehensive IP range blocking for Cisco Systems (50+ ranges) and FireEye/Mandiant infrastructure (30+ ranges) including security tools and threat intelligence services.</span>
                            </div>
                            <div className="tech-item">
                                <strong>Enterprise Vendor Protection:</strong>
                                <span>Blocks 100+ security vendors including Symantec, McAfee, Kaspersky, Norton, CrowdStrike, SentinelOne, vulnerability scanners (Nessus, OpenVAS, Qualys), and SIEM platforms (Splunk, QRadar, ArcSight).</span>
                            </div>
                            <div className="tech-item">
                                <strong>Cloud Platform Intelligence:</strong>
                                <span>Smart detection and blocking of Google, Microsoft, AWS automated services while distinguishing between legitimate human traffic and security crawlers/bots.</span>
                            </div>
                            <div className="tech-item">
                                <strong>Advanced Bot Analysis:</strong>
                                <span>200+ user agent patterns, headless browser detection (Chrome, Firefox), automation tools (Selenium, Puppeteer, Playwright), and comprehensive threat scoring with geolocation intelligence.</span>
                            </div>
                            <div className="tech-item">
                                <strong>Rate Limiting & Analytics:</strong>
                                <span>Configurable request throttling (50 req/min default), real-time threat scoring, behavioral analysis, and comprehensive logging with geolocation-based threat intelligence integration.</span>
                            </div>
                        </div>
                    </div>

                    <div className="current-configuration">
                        <h6 className="section-title">Enterprise Configuration Details</h6>
                        <div className="config-details">
                            <div className="config-item">
                                <span className="config-label">Protection Status:</span>
                                <span className="config-value enabled">Enterprise Grade Active</span>
                            </div>
                            <div className="config-item">
                                <span className="config-label">Security Coverage:</span>
                                <span className="config-value">100+ Vendors Blocked (Cisco, FireEye, Symantec, McAfee, etc.)</span>
                            </div>
                            <div className="config-item">
                                <span className="config-label">Bot Patterns:</span>
                                <span className="config-value">200+ Detection Signatures Active</span>
                            </div>
                            <div className="config-item">
                                <span className="config-label">Rate Limiting:</span>
                                <span className="config-value">50 requests/minute with threat scoring</span>
                            </div>
                            <div className="config-item">
                                <span className="config-label">Cloud Protection:</span>
                                <span className="config-value">Google, Microsoft, AWS automated services blocked</span>
                            </div>
                            <div className="config-item">
                                <span className="config-label">Threat Intelligence:</span>
                                <span className="config-value">Real-time geolocation & IP reputation analysis</span>
                            </div>
                        </div>
                    </div>

                    <div className="enterprise-coverage">
                        <h6 className="section-title">Comprehensive Enterprise Security Vendor Coverage</h6>
                        <div className="vendor-coverage-grid">
                            <div className="vendor-category">
                                <h6 className="vendor-category-title">🏢 Network Infrastructure & Firewalls</h6>
                                <ul className="vendor-list">
                                    <li><strong>Cisco Systems (50+ IP Ranges):</strong> 128.107.x.x, 64.100-104.x.x, 171.68-70.x.x, 192.135.x.x, 207.172.x.x - Umbrella DNS Security, Stealthwatch Network Analytics, Meraki Cloud Management, ASA Firewalls, Firepower Threat Defense, AnyConnect VPN, WebEx Security</li>
                                    <li><strong>Juniper Networks:</strong> SRX Series Firewalls, EX Series Switches, QFX Data Center, MX Universal Routing, Contrail Networking, Sky ATP Advanced Threat Prevention, JATP (Juniper Advanced Threat Prevention)</li>
                                    <li><strong>Fortinet (Security Fabric):</strong> FortiGate Next-Gen Firewalls, FortiOS Operating System, FortiAnalyzer Logging, FortiManager Central Management, FortiSandbox Malware Detection, FortiWeb Web Application Firewall</li>
                                    <li><strong>Palo Alto Networks:</strong> Next-Generation Firewalls, Panorama Management, Prisma Cloud Security, Cortex XDR, WildFire Malware Analysis, Traps Endpoint Protection, GlobalProtect VPN</li>
                                    <li><strong>Checkpoint Software:</strong> Quantum Security Gateways, SmartConsole Management, CloudGuard Cloud Security, Harmony Endpoint, SandBlast Network Protection, Mobile Threat Prevention</li>
                                </ul>
                            </div>
                            <div className="vendor-category">
                                <h6 className="vendor-category-title"><span>🔥</span> Advanced Threat Intelligence & EDR</h6>
                                <ul className="vendor-list">
                                    <li><strong>FireEye/Mandiant (30+ IP Ranges):</strong> 199.19.248-249.x.x, 162.220-223.x.x, 170.224-225.x.x - Threat Intelligence Feeds, Malware Analysis Platform, Incident Response Services, Red Team Assessment Tools, Continuous Monitoring</li>
                                    <li><strong>CrowdStrike Falcon Platform:</strong> Endpoint Detection & Response, Threat Hunting Services, Falcon Intelligence Premium, Falcon OverWatch Managed Hunting, Cloud Security Posture Management, Identity Protection</li>
                                    <li><strong>SentinelOne Singularity:</strong> Behavioral AI Detection, Autonomous Response Engine, Ranger AD Assessment, Vigilance Managed Detection, Cloud Workload Protection, IoT Security Coverage</li>
                                    <li><strong>Carbon Black (VMware):</strong> Enterprise EDR Platform, Threat Hunting Capabilities, Behavioral Monitoring, Live Response Tools, Cloud Endpoint Standard, App Control Application Whitelisting</li>
                                    <li><strong>Microsoft Defender:</strong> ATP Advanced Threat Protection, Endpoint Detection Response, Cloud App Security, Identity Protection, Office 365 Security, Azure Security Center Integration</li>
                                </ul>
                            </div>
                            <div className="vendor-category">
                                <h6 className="vendor-category-title"><span>🦠</span> Antivirus & Endpoint Protection</h6>
                                <ul className="vendor-list">
                                    <li><strong>Symantec/Broadcom:</strong> Endpoint Protection Complete, Data Loss Prevention (DLP), Email Security.Cloud, Web Security Service, CloudSOC Cloud Access Security, Industrial Control System Protection</li>
                                    <li><strong>McAfee/Trellix:</strong> MVISION Endpoint Detection, Total Protection Enterprise, Web Gateway Appliances, Database Security Platform, SIEM Enterprise Security Manager, Network Security Platform</li>
                                    <li><strong>Kaspersky Security:</strong> Endpoint Security Cloud, Anti-Targeted Attack Platform, Threat Intelligence Portal, Forensics & Malware Analysis, Industrial CyberSecurity, Hybrid Cloud Security</li>
                                    <li><strong>Norton/NortonLifeLock:</strong> Norton 360 Enterprise, LifeLock Identity Protection, Norton Security Online, Small Business Solutions, Mobile Security Enterprise</li>
                                    <li><strong>ESET Enterprise:</strong> PROTECT Complete, DETECT & RESPOND XDR, Cloud Office Security, Mail Security, Mobile Device Management, Encryption Solutions</li>
                                </ul>
                            </div>
                            <div className="vendor-category">
                                <h6 className="vendor-category-title"><span>🔍</span> Vulnerability Management & Penetration Testing</h6>
                                <ul className="vendor-list">
                                    <li><strong>Tenable Nessus Professional:</strong> Vulnerability Scanning Engine, Compliance Assessments, Web Application Testing, Industrial Security Scanning, Cloud Infrastructure Assessment, Container Security</li>
                                    <li><strong>Rapid7 Security Platform:</strong> Nexpose Vulnerability Management, InsightVM Risk-Based Vulnerability Management, Metasploit Penetration Testing, InsightIDR SIEM & Security Analytics, AppSpider Dynamic Testing</li>
                                    <li><strong>Qualys Cloud Platform:</strong> VMDR Vulnerability Management, Web Application Scanning, Container Security Assessment, Policy Compliance Scanning, Certificate Assessment, Patch Management</li>
                                    <li><strong>OpenVAS Community:</strong> Open Source Vulnerability Scanner, Greenbone Security Manager, Security Configuration Assessment, Network Discovery Tools, Compliance Reporting</li>
                                    <li><strong>Burp Suite Professional:</strong> Web Application Security Testing, Automated Scanning, Manual Testing Tools, Extensibility Framework, Enterprise Edition Collaboration</li>
                                </ul>
                            </div>
                            <div className="vendor-category">
                                <h6 className="vendor-category-title"><span>📊</span> SIEM & Security Analytics</h6>
                                <ul className="vendor-list">
                                    <li><strong>Splunk Enterprise Security:</strong> Security Information Event Management, User Behavior Analytics, Threat Intelligence Framework, Security Orchestration, Phantom SOAR Integration</li>
                                    <li><strong>IBM QRadar SIEM:</strong> Security Intelligence Platform, Network Security Monitoring, Log Management, Threat Detection, Risk Management, SOAR Integration</li>
                                    <li><strong>ArcSight Enterprise Security:</strong> Logger Event Collection, Connector Framework, Correlation Engine, Transformation Hub, Investigate Forensics Platform</li>
                                    <li><strong>Elastic Security (ELK):</strong> SIEM Solutions, Endpoint Security, Cloud Monitoring, Threat Hunting, Machine Learning Detection, SOAR Capabilities</li>
                                    <li><strong>LogRhythm NextGen:</strong> SIEM Platform, Network & Endpoint Monitoring, User Entity Behavior Analytics, Security Orchestration, Threat Lifecycle Management</li>
                                </ul>
                            </div>
                            <div className="vendor-category">
                                <h6 className="vendor-category-title"><span>☁️</span> Cloud Security & Threat Intelligence</h6>
                                <ul className="vendor-list">
                                    <li><strong>Google Cloud Security:</strong> Security Command Center, Web Security Scanner, Binary Authorization, Container Analysis API, Chronicle Security Analytics, reCAPTCHA Enterprise</li>
                                    <li><strong>Microsoft Azure Security:</strong> Security Center, Sentinel Cloud SIEM, Key Vault, Information Protection, Conditional Access, Cloud App Security</li>
                                    <li><strong>Amazon Web Services:</strong> GuardDuty Threat Detection, Inspector Assessment, Security Hub, CloudTrail Logging, WAF Web Application Firewall, Shield DDoS Protection</li>
                                    <li><strong>VirusTotal Intelligence:</strong> File & URL Analysis, Threat Intelligence Feeds, YARA Rule Detection, Graph API Relationships, Enterprise Hunting Platform</li>
                                    <li><strong>Shodan Search Engine:</strong> Internet-Connected Device Discovery, Industrial Control Systems, IoT Device Identification, Network Reconnaissance, Banner Information</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {!botDetectionEnabled && (
                <div className="bot-detection-content">
                    <div className="protection-status-section">
                        <div className="status-card disabled">
                            <div className="status-icon"><span>⚠️</span></div>
                            <div className="status-content">
                                <h6>Enterprise Protection Disabled</h6>
                                <p>Advanced bot detection is currently disabled. Enable it to activate comprehensive protection against automated threats and security scanners.</p>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BotDetection;